import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BookOpen,
  Edit2,
  Folder,
  FolderPlus,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  createMasterVocab,
  createTopic,
  deleteMasterVocab,
  deleteTopic,
  getMasterVocabs,
  getTopics,
  importMasterVocabs,
  updateMasterVocab,
  updateTopic,
} from "../../services/MasterVocabService";

const HSK_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const EMPTY_VOCAB = {
  word: "",
  pinyin: "",
  meaning: "",
  definition: "",
  example: "",
  hskLevel: 1,
  topicId: "",
};

const EMPTY_TOPIC = { name: "", icon: "" };

// ── VocabModal ────────────────────────────────────────────────────────────────
function VocabModal({ vocab, topics, onClose, onSaved }) {
  const [form, setForm] = useState(vocab ? { ...vocab, topicId: vocab.topicId?._id || vocab.topicId || "" } : { ...EMPTY_VOCAB });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.word.trim() || !form.meaning.trim()) {
      toast.error("Từ và nghĩa là bắt buộc");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, topicId: form.topicId || null };
      if (vocab?._id) {
        await updateMasterVocab(vocab._id, payload);
        toast.success("Cập nhật từ vựng thành công");
      } else {
        await createMasterVocab(payload);
        toast.success("Thêm từ vựng thành công");
      }
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Có lỗi xảy ra");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">{vocab?._id ? "Sửa từ vựng" : "Thêm từ vựng"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Từ *</label>
              <input name="word" value={form.word} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="汉字" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pinyin</label>
              <input name="pinyin" value={form.pinyin} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="hànzì" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nghĩa *</label>
            <input name="meaning" value={form.meaning} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="Nghĩa tiếng Việt" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Định nghĩa</label>
            <input name="definition" value={form.definition} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="Giải thích chi tiết (tuỳ chọn)" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ví dụ</label>
            <input name="example" value={form.example} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="Câu ví dụ (tuỳ chọn)" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cấp độ HSK</label>
              <select name="hskLevel" value={form.hskLevel} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500">
                {HSK_LEVELS.map((l) => (
                  <option key={l} value={l}>HSK {l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
              <select name="topicId" value={form.topicId} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500">
                <option value="">-- Không có --</option>
                {topics.map((t) => (
                  <option key={t._id} value={t._id}>{t.icon ? `${t.icon} ` : ""}{t.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Huỷ</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
              {saving ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── TopicModal ────────────────────────────────────────────────────────────────
function TopicModal({ topic, onClose, onSaved }) {
  const [form, setForm] = useState(topic ? { name: topic.name, icon: topic.icon || "" } : { ...EMPTY_TOPIC });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Tên topic là bắt buộc"); return; }
    setSaving(true);
    try {
      if (topic?._id) {
        await updateTopic(topic._id, form);
        toast.success("Cập nhật topic thành công");
      } else {
        await createTopic(form);
        toast.success("Tạo topic thành công");
      }
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Có lỗi xảy ra");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">{topic?._id ? "Sửa topic" : "Tạo topic"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tên topic *</label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="VD: Du lịch" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Icon (emoji)</label>
            <input value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="VD: ✈️" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Huỷ</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
              {saving ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── ImportModal ───────────────────────────────────────────────────────────────
function ImportModal({ topics, onClose, onImported }) {
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const readCellCI = (row, aliases) => {
    const entries = Object.entries(row || {});
    for (const [key, value] of entries) {
      const normalized = String(key || "").trim().toLowerCase();
      if (aliases.includes(normalized)) {
        return value;
      }
    }
    return "";
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(ev.target.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
      setRows(data.map((r) => ({
        word: String(readCellCI(r, ["word", "từ", "tu"]) || "").trim(),
        pinyin: String(readCellCI(r, ["pinyin"]) || "").trim(),
        meaning: String(readCellCI(r, ["meaning", "nghĩa", "nghia"]) || "").trim(),
        definition: String(readCellCI(r, ["definition", "định nghĩa", "dinh nghia"]) || "").trim(),
        example: String(readCellCI(r, ["example", "ví dụ", "vi du"]) || "").trim(),
        hskLevel: Number(readCellCI(r, ["hsklevel", "hsk level", "hsk", "cấp độ", "cap do"]) || 1),
        topicName: String(readCellCI(r, ["foldername", "folder", "topicname", "topic", "tên folder", "ten folder", "tên topic", "ten topic"]) || "").trim() || null,
      })));
    };
    reader.readAsBinaryString(file);
  };

  const downloadTemplate = () => {
    const data = [{ word: "汉语", pinyin: "Hànyǔ", meaning: "Tiếng Trung", definition: "", example: "我学汉语", hskLevel: 1, folderName: "Giao tiếp" }];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "vocab-import-template.xlsx");
  };

  const handleImport = async () => {
    if (rows.length === 0) { toast.error("Chưa có dữ liệu để import"); return; }
    setImporting(true);
    try {
      const res = await importMasterVocabs(rows);
      toast.success(`Import xong: ${res.data?.inserted} thêm mới, ${res.data?.skipped} bỏ qua`);
      if (res.data?.errors?.length > 0) {
        const detailLines = res.data.errors
          .slice(0, 15)
          .map((err, idx) => `${idx + 1}. Dòng ${err.row || "?"} - ${err.word || "(trống)"}: ${err.reason}`)
          .join("\n");
        const more = res.data.errors.length > 15 ? `\n... và ${res.data.errors.length - 15} lỗi khác` : "";
        window.alert(`Import có ${res.data.errors.length} lỗi:\n\n${detailLines}${more}`);
        toast.warn(`${res.data.errors.length} lỗi trong quá trình import`);
      }
      onImported();
    } catch (error) {
      const backendErrors = error?.response?.data?.errors;
      if (Array.isArray(backendErrors) && backendErrors.length > 0) {
        const detailLines = backendErrors
          .slice(0, 15)
          .map((err, idx) => `${idx + 1}. Dòng ${err.row || "?"} - ${err.word || "(trống)"}: ${err.reason}`)
          .join("\n");
        const more = backendErrors.length > 15 ? `\n... và ${backendErrors.length - 15} lỗi khác` : "";
        window.alert(`Import thất bại với ${backendErrors.length} lỗi:\n\n${detailLines}${more}`);
      }
      toast.error(error?.response?.data?.message || "Import thất bại");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">Import từ Excel</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <button onClick={downloadTemplate} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            <Upload size={14} /> Tải template Excel mẫu
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-red-50 file:text-red-600 hover:file:bg-red-100" />
          {rows.length > 0 && (
            <p className="text-sm text-gray-600">Đọc được <strong>{rows.length}</strong> dòng từ file</p>
          )}
          {rows.length > 0 && (
            <div className="overflow-auto max-h-48 border rounded-lg text-xs">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>{["Từ", "Pinyin", "Nghĩa", "Cấp"].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.slice(0, 5).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-1">{r.word}</td>
                      <td className="px-3 py-1">{r.pinyin}</td>
                      <td className="px-3 py-1">{r.meaning}</td>
                      <td className="px-3 py-1">{r.hskLevel}</td>
                    </tr>
                  ))}
                  {rows.length > 5 && <tr><td colSpan={4} className="px-3 py-1 text-gray-400">... và {rows.length - 5} dòng nữa</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Huỷ</button>
            <button onClick={handleImport} disabled={importing || rows.length === 0} className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
              {importing ? "Đang import..." : `Import ${rows.length} từ`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Vocabulary() {
  const [topics, setTopics] = useState([]);
  const [vocabs, setVocabs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedTopic, setSelectedTopic] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [hskFilter, setHskFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const LIMIT = 20;

  const [vocabModal, setVocabModal] = useState(null); // null | "new" | vocabObj
  const [topicModal, setTopicModal] = useState(null); // null | "new" | topicObj
  const [importModal, setImportModal] = useState(false);

  const fetchTopics = useCallback(async () => {
    try {
      const res = await getTopics();
      setTopics(res?.data || []);
    } catch {
      toast.error("Không thể tải danh sách topic");
    }
  }, []);

  const fetchVocabs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: LIMIT };
      if (appliedSearch) params.q = appliedSearch;
      if (selectedTopic) params.topicId = selectedTopic;
      if (hskFilter) params.hskLevel = hskFilter;
      const res = await getMasterVocabs(params);
      setVocabs(res?.data || []);
      setTotal(res?.total || 0);
    } catch {
      toast.error("Không thể tải từ vựng");
    } finally {
      setLoading(false);
    }
  }, [currentPage, appliedSearch, selectedTopic, hskFilter]);

  useEffect(() => { fetchTopics(); }, [fetchTopics]);
  useEffect(() => { fetchVocabs(); }, [fetchVocabs]);

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedSearch(searchQuery);
  };

  const handleDeleteVocab = async (id) => {
    if (!window.confirm("Xóa từ vựng này?")) return;
    try {
      await deleteMasterVocab(id);
      toast.success("Đã xóa");
      fetchVocabs();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Xóa thất bại");
    }
  };

  const handleDeleteTopic = async (id) => {
    if (!window.confirm("Xóa topic này? Các từ thuộc topic sẽ không còn topic.")) return;
    try {
      await deleteTopic(id);
      toast.success("Đã xóa topic");
      fetchTopics();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Xóa thất bại");
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="flex h-full bg-gray-50">
      {/* ── Sidebar Topics ── */}
      <aside className="w-56 shrink-0 bg-white border-r flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <span className="font-semibold text-sm text-gray-700 flex items-center gap-2">
            <Folder size={16} /> Topics
          </span>
          <button onClick={() => setTopicModal("new")} className="text-red-600 hover:bg-red-50 rounded p-1">
            <FolderPlus size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          <button
            onClick={() => { setSelectedTopic(""); setCurrentPage(1); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedTopic === "" ? "bg-red-50 text-red-600 font-medium" : "hover:bg-gray-100 text-gray-700"}`}
          >
            Tất cả
          </button>
          {topics.map((t) => (
            <div key={t._id} className={`flex items-center gap-1 rounded-lg ${selectedTopic === t._id ? "bg-red-50" : "hover:bg-gray-100"}`}>
              <button
                onClick={() => { setSelectedTopic(t._id); setCurrentPage(1); }}
                className={`flex-1 text-left px-3 py-2 text-sm transition-colors ${selectedTopic === t._id ? "text-red-600 font-medium" : "text-gray-700"}`}
              >
                {t.icon ? `${t.icon} ` : ""}{t.name}
              </button>
              <button onClick={() => setTopicModal(t)} className="p-1 text-gray-400 hover:text-blue-500"><Edit2 size={13} /></button>
              <button onClick={() => handleDeleteTopic(t._id)} className="p-1 text-gray-400 hover:text-red-500 mr-1"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <BookOpen size={20} className="text-red-600" />
            <h1 className="text-lg font-bold text-gray-800">Kho từ vựng Master</h1>
            <span className="text-sm text-gray-500">({total} từ)</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center border rounded-lg overflow-hidden">
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Tìm từ, pinyin, nghĩa..."
                className="px-3 py-2 text-sm outline-none w-48"
              />
              <button onClick={handleSearch} className="px-3 py-2 bg-gray-50 hover:bg-gray-100 border-l">
                <Search size={16} className="text-gray-500" />
              </button>
            </div>
            <select value={hskFilter} onChange={(e) => { setHskFilter(e.target.value); setCurrentPage(1); }} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">Tất cả cấp</option>
              {HSK_LEVELS.map((l) => <option key={l} value={l}>HSK {l}</option>)}
            </select>
            <button onClick={() => setImportModal(true)} className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50">
              <Upload size={15} /> Import Excel
            </button>
            <button onClick={() => setVocabModal("new")} className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">
              <Plus size={15} /> Thêm từ
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex justify-center py-20 text-gray-400">Đang tải...</div>
          ) : vocabs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <BookOpen size={40} className="mb-3 opacity-40" />
              <p>Chưa có từ vựng nào</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    {["Từ", "Pinyin", "Nghĩa", "Ví dụ", "Cấp", "Topic", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {vocabs.map((v) => (
                    <tr key={v._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900 text-base">{v.word}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{v.pinyin}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-[180px] truncate">{v.meaning}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[160px] truncate">{v.example}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          HSK {v.hskLevel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {v.topicId ? `${v.topicId.icon ? `${v.topicId.icon} ` : ""}${v.topicId.name}` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setVocabModal(v)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <Edit2 size={15} />
                          </button>
                          <button onClick={() => handleDeleteVocab(v._id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <button disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-40">← Trước</button>
              <span className="px-3 py-1.5 text-sm text-gray-600">{currentPage} / {totalPages}</span>
              <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-40">Sau →</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {vocabModal && (
        <VocabModal
          vocab={vocabModal === "new" ? null : vocabModal}
          topics={topics}
          onClose={() => setVocabModal(null)}
          onSaved={() => { setVocabModal(null); fetchVocabs(); }}
        />
      )}
      {topicModal && (
        <TopicModal
          topic={topicModal === "new" ? null : topicModal}
          onClose={() => setTopicModal(null)}
          onSaved={() => { setTopicModal(null); fetchTopics(); }}
        />
      )}
      {importModal && (
        <ImportModal
          topics={topics}
          onClose={() => setImportModal(false)}
          onImported={() => { setImportModal(false); fetchVocabs(); }}
        />
      )}
    </div>
  );
}
